import React, { useCallback, useEffect, useRef, useState } from 'react';
import ListingTemplatePickerModal from '@/pages/ipe/workbench/modals/ListingTemplatePickerModal';
import ExportPreviewModal from '@/pages/ipe/workbench/modals/ExportPreviewModal';
import { exportToTemplate } from '@/utils/exportUtils';
import { addDownloadCenterRecord, patchDownloadCenterRecord } from '@/utils/downloadCenter';
import { loadTableListingTemplatesScoped } from '@/pages/ipe/workbench/utils/loadTableListingTemplatesScoped';
import { queueListingTemplateExcelToDownloadCenter } from '@/pages/ipe/workbench/utils/listingTemplateExportDownload';
import { getIpeErrorMessage } from '@/pages/ipe/workbench/utils/errorHandler';
import { useIpeWorkspaceMode } from './useIpeWorkspaceMode';
import i18n from '@/i18n';

/**
 * 详情页共用「按模板导出」：选表格模板 → 预览 → 后台导出至下载中心。
 * @param {{ showToast?: (message: string, type?: string) => void }} [options]
 */
export default function useListingTemplateExport({ showToast } = {}) {
  const workspaceMode = useIpeWorkspaceMode();
  const [isListingTemplatePickerOpen, setIsListingTemplatePickerOpen] = useState(false);
  const [listingTemplates, setListingTemplates] = useState([]);
  const [listingTemplateGroups, setListingTemplateGroups] = useState([]);
  const [selectedListingTemplateGroup, setSelectedListingTemplateGroup] = useState('');
  const [selectedListingTemplatePlatform, setSelectedListingTemplatePlatform] = useState('');
  const [selectedListingTemplateIds, setSelectedListingTemplateIds] = useState([]);
  const [isExportPreviewOpen, setIsExportPreviewOpen] = useState(false);
  const [exportPreviewData, setExportPreviewData] = useState([]);

  const buildPreviewRef = useRef(null);
  const enrichRowsRef = useRef(null);
  const exportInProgressRef = useRef(false);

  const toast = useCallback((message, type = 'info') => {
    showToast?.(message, type);
  }, [showToast]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // 跟随顶栏个人/团队工作区，只加载当前数据源下的表格模板
        const { templates: templatesList, groups } = await loadTableListingTemplatesScoped();
        if (cancelled) return;
        setListingTemplates(templatesList);
        setListingTemplateGroups(groups);
        // 切换工作区后清空已选，避免跨数据源残留选中
        setSelectedListingTemplateIds([]);
        setSelectedListingTemplateGroup('');
        setSelectedListingTemplatePlatform('');
      } catch (error) {
        console.error('[按模板导出] 加载表格模板失败:', error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [workspaceMode]);

  const resetPickerSelection = useCallback(() => {
    setSelectedListingTemplateIds([]);
    setSelectedListingTemplateGroup('');
    setSelectedListingTemplatePlatform('');
  }, []);

  /**
   * @param {() => Promise<object[]>} buildPreviewRows
   * @param {{ enrichRows?: (rows: object[]) => Promise<object[]> }} [options]
   */
  const openExportPicker = useCallback((buildPreviewRows, options = {}) => {
    if (typeof buildPreviewRows !== 'function') {
      toast(i18n.t('workbench:templateExportToast.invalidConfig'), 'error');
      return;
    }
    buildPreviewRef.current = buildPreviewRows;
    enrichRowsRef.current = typeof options.enrichRows === 'function' ? options.enrichRows : null;
    resetPickerSelection();
    setIsListingTemplatePickerOpen(true);
  }, [resetPickerSelection, toast]);

  const closePicker = useCallback(() => {
    setIsListingTemplatePickerOpen(false);
    resetPickerSelection();
    buildPreviewRef.current = null;
    enrichRowsRef.current = null;
  }, [resetPickerSelection]);

  const closePreview = useCallback(() => {
    setIsExportPreviewOpen(false);
    setExportPreviewData([]);
  }, []);

  const handlePickerConfirm = useCallback(async () => {
    if (selectedListingTemplateIds.length === 0) {
      toast(i18n.t('workbench:templateExportToast.selectAtLeastOneTable'), 'warning');
      return;
    }
    if (typeof buildPreviewRef.current !== 'function') {
      toast(i18n.t('workbench:templateExportToast.invalidConfig'), 'error');
      return;
    }
    try {
      const previewData = await buildPreviewRef.current();
      if (!Array.isArray(previewData) || previewData.length === 0) {
        toast(i18n.t('workbench:templateExportToast.noData'), 'warning');
        return;
      }
      setIsListingTemplatePickerOpen(false);
      setExportPreviewData(previewData);
      setIsExportPreviewOpen(true);
    } catch (error) {
      console.error('[按模板导出] 构建预览失败:', error);
      toast(i18n.t('workbench:templateExportToast.buildPreviewFailed', { message: getIpeErrorMessage(error, i18n.t('workbench:ipeUtilError.retryGeneric')) }), 'error');
    }
  }, [selectedListingTemplateIds, toast]);

  const handleExport = useCallback((selectedData) => {
    if (exportInProgressRef.current) return;
    if (!selectedData?.length) {
      toast(i18n.t('workbench:templateExportToast.selectData'), 'warning');
      return;
    }
    if (selectedListingTemplateIds.length === 0) {
      toast(i18n.t('workbench:templateExportToast.selectAtLeastOneTable'), 'warning');
      return;
    }

    const templateIds = [...selectedListingTemplateIds];
    const enrichFn = enrichRowsRef.current;
    const recordId = `dl-${Date.now()}-listing-${Math.random().toString(36).slice(2, 8)}`;
    const selectedTemplateNames = listingTemplates
      .filter((tpl) => templateIds.includes(tpl.id))
      .map((tpl) => String(tpl.name || tpl.title || '').trim())
      .filter(Boolean);
    const interimFileName = selectedTemplateNames.length === 1
      ? `${selectedTemplateNames[0]}_导出中…`
      : selectedTemplateNames.length > 1
        ? `${selectedTemplateNames[0]}等${selectedTemplateNames.length}个模板_导出中…`
        : '表格导出中…';

    addDownloadCenterRecord({
      id: recordId,
      fileName: interimFileName,
      url: '',
      source: '按模板导出',
      status: 'downloading',
      createdAt: new Date().toISOString(),
      completedAt: null,
      downloadedAt: null,
      error: null,
      progress: 5,
      itemCount: templateIds.length,
    });

    closePreview();
    resetPickerSelection();
    buildPreviewRef.current = null;
    enrichRowsRef.current = null;
    toast(i18n.t('workbench:templateExportToast.queued'), 'info');

    exportInProgressRef.current = true;
    (async () => {
      try {
        let dataWithImages = selectedData;
        if (typeof enrichFn === 'function') {
          patchDownloadCenterRecord(recordId, { progress: 20 });
          dataWithImages = await enrichFn(selectedData);
        }

        patchDownloadCenterRecord(recordId, { progress: 40 });
        // 按模板导出只把 Excel 写入下载中心，图片 URL 仍写入表格单元格，不再单独打包图片
        const { excelFiles } = await exportToTemplate(dataWithImages, templateIds, () => {}, {
          deferExcelDownloads: true,
          runImageDownloads: false,
        });

        patchDownloadCenterRecord(recordId, { progress: 80 });
        await queueListingTemplateExcelToDownloadCenter(excelFiles, {
          source: '按模板导出',
          recordId,
        });

        const excelCount = Array.isArray(excelFiles) ? excelFiles.length : 0;
        toast(i18n.t('workbench:templateExportToast.done', { count: excelCount }), 'success');
      } catch (error) {
        patchDownloadCenterRecord(recordId, {
          status: 'failed',
          error: getIpeErrorMessage(error, '导出失败'),
          completedAt: new Date().toISOString(),
        });
        toast(i18n.t('workbench:templateExportToast.failed', { message: getIpeErrorMessage(error, i18n.t('workbench:ipeUtilError.retryGeneric')) }), 'error');
      } finally {
        exportInProgressRef.current = false;
      }
    })();
  }, [closePreview, listingTemplates, resetPickerSelection, selectedListingTemplateIds, toast]);

  const ListingTemplateExportModals = (
    <>
      <ListingTemplatePickerModal
        isOpen={isListingTemplatePickerOpen}
        onCancel={closePicker}
        onConfirm={handlePickerConfirm}
        groups={listingTemplateGroups}
        selectedGroup={selectedListingTemplateGroup}
        onGroupChange={setSelectedListingTemplateGroup}
        templates={listingTemplates}
        selectedTemplateIds={selectedListingTemplateIds}
        onToggleTemplate={(tpl) => {
          setSelectedListingTemplateIds((prev) => {
            if (prev.includes(tpl.id)) {
              return prev.filter((id) => id !== tpl.id);
            }
            return [...prev, tpl.id];
          });
        }}
        selectedPlatform={selectedListingTemplatePlatform}
        onPlatformChange={setSelectedListingTemplatePlatform}
      />
      <ExportPreviewModal
        isOpen={isExportPreviewOpen}
        onCancel={closePreview}
        onExport={handleExport}
        previewData={exportPreviewData}
      />
    </>
  );

  return {
    openExportPicker,
    ListingTemplateExportModals,
    isExportUiOpen: isListingTemplatePickerOpen || isExportPreviewOpen,
    isListingTemplatePickerOpen,
    isExportPreviewOpen,
  };
}
